<template>
  <div class="container-fluid">
    <!--
    <div class="row my-3">
      <div class="col">
        <h4 class="p-0 m-0">Work Report</h4>
      </div>
    </div>
    -->

    <!-- Filter Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">Technician</label>
                <tech-ac v-model="filters.techId" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Date Range Preset</label>
                <select 
                  class="form-select form-select-sm" 
                  v-model="datePreset"
                  @change="applyDatePreset"
                >
                  <option value="">Custom Range</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this-week">This Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-and-last-week">This Week and Last Week</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Start Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.startDate"
                />
              </div>
              <div class="col-md-2">
                <label class="form-label">End Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.endDate"
                />
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <button 
                  class="btn btn-sm btn-outline-primary w-100" 
                  @click="runReport"
                  :disabled="!filters.techId || !filters.startDate || !filters.endDate || loading"
                >
                  <i class="fa fa-play me-2"></i>
                  {{ loading ? 'Loading...' : 'Run Report' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading work entries...</p>
    </div>

    <!-- Report Results -->
    <div v-if="reportData && !loading" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Technician Summary Header -->
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead class="table-dark">
                  <tr>
                    <th>Technician</th>
                    <th class="text-end">ST (Standard Time)</th>
                    <th class="text-end">OT (Over Time)</th>
                    <th class="text-end">SB (Should Bill)</th>
                    <th class="text-end">Tot (Total)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="table-secondary fw-bold">
                    <td>{{ technicianName }}</td>
                    <td class="text-end">{{ formatHours(totals.standardTime) }}</td>
                    <td class="text-end">{{ formatHours(totals.overTime) }}</td>
                    <td class="text-end">{{ formatCurrency(totals.shouldBill) }}</td>
                    <td class="text-end">{{ formatCurrency(totals.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Daily Breakdown -->
            <div v-for="day in reportData" :key="day.date" class="mb-4">
              <!-- Day Summary -->
              <table class="table table-bordered mb-0">
                <thead class="table-primary">
                  <tr>
                    <th style="width: 200px;">{{ formatDate(day.date) }}</th>
                    <th class="text-end" style="width: 150px;">ST: {{ formatHours(day.totals.standardTime) }}</th>
                    <th class="text-end" style="width: 150px;">OT: {{ formatHours(day.totals.overTime) }}</th>
                    <th class="text-end" style="width: 150px;">SB: {{ formatCurrency(day.totals.shouldBill) }}</th>
                    <th class="text-end" style="width: 150px;">Tot: {{ formatCurrency(day.totals.total) }}</th>
                  </tr>
                </thead>
              </table>

              <!-- Status Groups -->
              <div v-for="group in day.statusGroups" :key="group.status" class="ms-3">
                <table class="table table-bordered table-sm mb-0">
                  <thead :class="getStatusHeaderClass(group.status)">
                    <tr>
                      <th colspan="5">{{ group.status || 'No Status' }}</th>
                    </tr>
                  </thead>
                </table>

                <!-- Tasks within Status Group -->
                <div v-for="task in group.tasks" :key="task.Id" class="ms-3">
                  <table class="table table-bordered table-sm mb-0">
                    <tbody>
                      <tr :class="getTaskStatusRowClass(task)">
                        <td style="width: 250px;"><strong>{{ task.CompanyName }}</strong></td>
                        <td style="width: 300px;">
                          <a href="#" @click.prevent="openJobModal(task.RequestId)" class="text-decoration-none" title="Open Job">
                            <ExternalLink :size="14" class="me-1 text-primary" />
                          </a>
                          Job #{{ task.RequestId }}: {{ task.RequestDescription }}
                        </td>
                        <td class="text-end" style="width: 150px;">ST: {{ formatHours(task.ActualTime - (task.ActualOvertime || 0)) }}</td>
                        <td class="text-end" style="width: 150px;">OT: {{ formatHours(task.ActualOvertime || 0) }}</td>
                        <td class="text-end" style="width: 150px;">SB: {{ formatCurrency(task.ShouldBill || 0) }}</td>
                        <td class="text-end" style="width: 150px;">Tot: {{ formatCurrency((task.ShouldBill || 0) + (task.PaidAmount || 0)) }}</td>
                      </tr>
                      <tr :class="getTaskStatusRowClass(task)">
                        <td colspan="6" class="small">
                          <div v-if="task.WorkAssignment" class="mb-1">
                            <strong>Work Assignment:</strong> {{ task.WorkAssignment }}
                          </div>
                          <div v-if="task.ActualWork">
                            <strong>Actual Work:</strong> 
                            <a href="#" @click.prevent="openWorkEditModal(task.Id)" class="text-decoration-none" title="Edit Work Entry">
                              {{ task.ActualWork }}
                              <Edit :size="12" class="ms-1 text-secondary" />
                            </a>
                          </div>
                          <div v-else>
                            <a href="#" @click.prevent="openWorkEditModal(task.Id)" class="text-decoration-none text-muted" title="Edit Work Entry">
                              <Edit :size="12" class="me-1" />Add work description
                            </a>
                          </div>
                          <div class="d-flex justify-content-between align-items-center mt-1">
                            <div class="text-muted">
                              <small><strong>Status:</strong> {{ getTaskStatusText(task) }}</small>
                            </div>
                            <button 
                              class="btn btn-sm btn-outline-primary"
                              @click="openReviewModal(task)"
                            >
                              <ClipboardCheck :size="14" class="me-1" />Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Management Review Modal -->
    <management-review-form 
      :visible="showReviewModal"
      :time-entry="reviewTask"
      @updated="onReviewSaved"
      @close="closeReviewModal"
    />

    <!-- Work Edit Modal -->
    <work-edit-embedded
      v-if="showWorkEditModal"
      :record-id="workEditRecordId"
      @close="closeWorkEditModal"
    />

    <!-- No Results Message -->
    <div v-if="!reportData && !loading && ranReport" class="row">
      <div class="col-12">
        <div class="alert alert-info">
          <i class="fa fa-info-circle me-2"></i>
          No time entries found for the selected criteria.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TechAc from '@/components/Techs/TechAc.vue';
import Api from '@/services/Api';
import Helper from '@/services/Helper';
import Emitter from '@/services/Emitter';
import { useAppStore } from '@/stores/AppStore';
import { useJobStatusStore } from '@/stores/JobStatusStore';
import { useTaskStatusStore } from '@/stores/TaskStatusStore';
import { ExternalLink, Edit, ClipboardCheck } from 'lucide-vue-next';
import ManagementReviewForm from '@/components/Reports/ManagementReviewForm.vue';
import WorkEditEmbedded from '@/components/Work/WorkEditEmbedded.vue';

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const jobStatusStore = useJobStatusStore();
const taskStatusStore = useTaskStatusStore();

const filters = ref({
  techId: null,
  startDate: null,
  endDate: null
});

const datePreset = ref('this-week');

const loading = ref(false);
const ranReport = ref(false);
const rawData = ref([]);
const technicianName = ref('');

// Flag to prevent watcher from triggering when dates are changed programmatically
let ignoreWatcher = false;

// Function to apply date presets
const applyDatePreset = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  let startDate, endDate;
  
  switch (datePreset.value) {
    case 'today':
      // Today only
      startDate = new Date(today);
      endDate = new Date(today);
      break;
      
    case 'yesterday':
      // Yesterday only
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;
      
    case 'this-week':
      // Sunday of this week to Saturday of this week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - dayOfWeek + 6);
      break;
      
    case 'last-week':
      // Sunday to Saturday of last week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek - 7);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - dayOfWeek - 1);
      break;
      
    case 'this-month':
      // First day of this month to today
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today);
      break;
      
    case 'last-month':
      // First day to last day of last month
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
      
    case 'this-and-last-week':
      // Sunday of last week to today
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek - 7);
      endDate = new Date(today);
      break;
      
    default:
      // Custom range - don't change dates
      return;
  }
  
  // Set flag to ignore watcher when we programmatically change dates
  ignoreWatcher = true;
  filters.value.startDate = Helper.dateToInput(startDate, Helper.DateFormat_Date);
  filters.value.endDate = Helper.dateToInput(endDate, Helper.DateFormat_Date);
};

// Initialize dates to current week
applyDatePreset();

// Watch for manual date changes and reset preset to custom
watch([() => filters.value.startDate, () => filters.value.endDate], () => {
  // Ignore changes triggered by preset selection
  if (ignoreWatcher) {
    ignoreWatcher = false;
    return;
  }
  
  // User manually changed dates, reset to custom
  if (datePreset.value) {
    datePreset.value = '';
  }
});

// Computed: Organize data by day and status
const reportData = computed(() => {
  if (!rawData.value || rawData.value.length === 0) return null;

  const dayMap = {};

  rawData.value.forEach(task => {
    const date = task.WorkCompleteDate ? task.WorkCompleteDate.split('T')[0] : 'Unknown';
    const statusId = task.Request?.RequestStatus;
    const status = getJobStatusName(statusId);

    if (!dayMap[date]) {
      dayMap[date] = {
        date,
        statusGroups: {},
        totals: { standardTime: 0, overTime: 0, shouldBill: 0, total: 0 }
      };
    }

    if (!dayMap[date].statusGroups[status]) {
      dayMap[date].statusGroups[status] = {
        status,
        tasks: []
      };
    }

    // Calculate totals
    const st = (task.ActualTime || 0) - (task.ActualOvertime || 0);
    const ot = task.ActualOvertime || 0;
    const sb = task.ShouldBill || 0;
    const tot = sb + (task.PaidAmount || 0);

    dayMap[date].totals.standardTime += st;
    dayMap[date].totals.overTime += ot;
    dayMap[date].totals.shouldBill += sb;
    dayMap[date].totals.total += tot;

    // Add task data
    dayMap[date].statusGroups[status].tasks.push({
      ...task,
      CompanyName: task.Contact?.Company || 'Unknown Company',
      RequestDescription: task.Request?.RequestDetailedDescription || task.Request?.TaskDetail || 'No Description'
    });
  });

  // Convert to array and sort by date
  return Object.values(dayMap)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(day => ({
      ...day,
      statusGroups: Object.values(day.statusGroups)
    }));
});

// Computed: Overall totals
const totals = computed(() => {
  if (!reportData.value) return { standardTime: 0, overTime: 0, shouldBill: 0, total: 0 };

  return reportData.value.reduce((acc, day) => ({
    standardTime: acc.standardTime + day.totals.standardTime,
    overTime: acc.overTime + day.totals.overTime,
    shouldBill: acc.shouldBill + day.totals.shouldBill,
    total: acc.total + day.totals.total
  }), { standardTime: 0, overTime: 0, shouldBill: 0, total: 0 });
});

const runReport = async () => {
  loading.value = true;
  ranReport.value = true;
  
  // Update URL with current filter parameters
  router.replace({
    query: {
      techId: filters.value.techId?.toString() || undefined,
      startDate: filters.value.startDate || undefined,
      endDate: filters.value.endDate || undefined,
      preset: datePreset.value || undefined
    }
  });
  
  try {
    const result = await Api.loadTimes({
      TechId: filters.value.techId,
      StartDate: filters.value.startDate + ' 00:00:00',
      EndDate: filters.value.endDate + ' 23:59:59',
      IncludeRequest: true,
      IncludeContact: true,
      TimeType: 'Work',
      Page: 0,
      Size: 999999
    });

    rawData.value = result || [];

    // Get technician name
    if (filters.value.techId) {
      const tech = await Api.loadTech(filters.value.techId);
      technicianName.value = tech?.TechnicianName || `Tech ${filters.value.techId}`;
    }
  } catch (error) {
    console.error('Error loading report:', error);
    rawData.value = [];
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateStr) => {
  // Parse the date string manually to avoid timezone issues
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const formatHours = (hours) => {
  if (!hours) return '0.00h';
  return `${parseFloat(hours).toFixed(2)}h`;
};

const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return `$${parseFloat(amount).toFixed(2)}`;
};

const getStatusHeaderClass = (status) => {
  // Normalize status for comparison (case-insensitive, trimmed)
  const normalizedStatus = (status || '').trim().toLowerCase();
  
  // Map normalized status to Bootstrap classes
  const statusMap = {
    'complete': 'table-primary',        // Blue
    'billable': 'table-success',        // Green
    'management review': 'table-warning', // Yellow
    'billed': 'table-success-dark',     // Dark Green (custom class)
    'in progress': 'table-info',
    'completed': 'table-primary',       // Blue
    'on hold': 'table-warning',
    'cancelled': 'table-danger',
    'pending': 'table-secondary'
  };
  
  return statusMap[normalizedStatus] || 'table-light';
};

const getStatusRowClass = (status) => {
  // Normalize status for comparison (case-insensitive, trimmed)
  const normalizedStatus = (status || '').trim().toLowerCase();
  
  // Debug: log status to see what we're getting
  if (normalizedStatus.includes('management') || normalizedStatus.includes('review')) {
    console.log('Status found:', status, '-> normalized:', normalizedStatus);
  }
  
  // Map normalized status to Bootstrap classes
  const statusMap = {
    'complete': 'table-primary',        // Blue
    'billable': 'table-success',        // Green
    'management review': 'table-warning', // Yellow
    'billed': 'table-success-dark',     // Dark Green (custom class)
    'in progress': 'table-info',
    'completed': 'table-primary',       // Blue
    'on hold': 'table-warning',
    'cancelled': 'table-danger',
    'pending': 'table-secondary'
  };
  
  return statusMap[normalizedStatus] || '';
};

const getJobStatusName = (statusId) => {
  if (!statusId) return 'No Status';
  const jobStatus = jobStatusStore.list[statusId];
  return jobStatus?.Status || `Status ${statusId}`;
};

const getTaskStatusText = (task) => {
  if (!task.Status && task.Status !== 0) return 'Open';
  const taskStatus = taskStatusStore.list[task.Status];
  return taskStatus?.Status || 'Unknown';
};

const getTaskStatusRowClass = (task) => {
  const statusText = getTaskStatusText(task);
  const normalizedStatus = (statusText || '').trim().toLowerCase();
  
  // Map task status to Bootstrap classes
  const statusMap = {
    'management review': 'table-warning',  // Yellow for Management Review
    'billable': 'table-success',           // Green
    'complete': 'table-primary',           // Blue
    'completed': 'table-primary',          // Blue
    'billed': 'table-success',             // Green
    'open': '',                            // No color
    'in progress': 'table-info',           // Light blue
    'on hold': 'table-secondary',          // Gray
    'cancelled': 'table-danger'            // Red
  };
  
  return statusMap[normalizedStatus] || '';
};

const openJobModal = (requestId) => {
  if (requestId) {
    Emitter.emit('edit:job', requestId);
  }
};

// Work Edit Modal
const showWorkEditModal = ref(false);
const workEditRecordId = ref(null);

const openWorkEditModal = (taskId) => {
  if (taskId) {
    workEditRecordId.value = String(taskId);
    showWorkEditModal.value = true;
  }
};

const closeWorkEditModal = () => {
  showWorkEditModal.value = false;
  workEditRecordId.value = null;
  // Refresh the report to show updated data
  if (ranReport.value) runReport();
};

// Review Modal
const showReviewModal = ref(false);
const reviewTask = ref(null);

const openReviewModal = (task) => {
  reviewTask.value = task;
  showReviewModal.value = true;
};

const closeReviewModal = () => {
  showReviewModal.value = false;
  reviewTask.value = null;
};

const onReviewSaved = () => {
  closeReviewModal();
  // Refresh the report to show updated data
  runReport();
};

// Initialize from URL params and run report if params present
const initFromUrlParams = () => {
  const { techId, startDate, endDate, preset } = route.query;
  
  if (techId) {
    filters.value.techId = parseInt(String(techId));
  }
  
  if (preset) {
    datePreset.value = String(preset);
    applyDatePreset();
  } else if (startDate && endDate) {
    ignoreWatcher = true;
    filters.value.startDate = String(startDate);
    filters.value.endDate = String(endDate);
    datePreset.value = '';
  }
  
  // Auto-run report if we have all required params
  return !!(techId && (startDate || preset) && (endDate || preset));
};

// Initialize stores on mount
onMounted(async () => {
  await jobStatusStore.load();
  await taskStatusStore.load();
  
  // Check URL params and auto-run if present
  const shouldAutoRun = initFromUrlParams();
  if (shouldAutoRun) {
    await runReport();
  }
});
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

/* Custom dark green for Billed status */
.table-success-dark {
  --bs-table-bg: #146c43;
  --bs-table-color: #fff;
}

.table th, .table td {
  vertical-align: middle;
}

.small {
  font-size: 0.875rem;
}
</style>
